/**
 * Живые обновления Переписок: ActionCable → правки кэша TanStack Query.
 *
 * Зеркало того, что делает панель Chatwoot (dashboard/helper/actionCable.js), но вместо
 * Vuex-хранилища правим кэш на месте. Что не удалось применить точечно (новая для списка
 * переписка, пропущенные события после обрыва) — дозагружаем запросом.
 */
import type { QueryClient } from '@tanstack/react-query';
import { MESSAGE_INCOMING, type InboxConversation } from '@/api/inboxTypes';
import { getConversation } from '@/api/inbox';
import config from './config';
import { Cable, type CableState } from './cable';
import { cwKeys, patchAllLists, putConversation, updateMessages } from './cwCache';
import { getOpenConversation } from './openConversation';
import { markRead } from './readMarker';
import { decodeEvent, applyMessageAction, type RtAction } from './realtimeEvents';

/** Не чаще раза в 5 с — как в панели (UNREAD_COUNTS_REFETCH_THROTTLE_MS). */
const UNREAD_THROTTLE_MS = 5000;

export interface RealtimeOptions {
  qc: QueryClient;
  accountId: number;
  userId: number;
  pubsubToken: string;
  onRejected?: () => void;
  onState?: (s: CableState) => void;
}

export interface RealtimeHandle {
  stop: () => void;
  reconnectNow: () => void;
  state: () => CableState;
}

export function cableUrl(chatwootUrl: string): string {
  return `${chatwootUrl.replace(/^http/, 'ws').replace(/\/$/, '')}/cable`;
}

/** Дозагрузка неизвестной переписки — один запрос на переписку за раз. */
const loading = new Set<string>();

async function fetchAndPut(qc: QueryClient, acc: number, id: number, userId: number): Promise<void> {
  const key = `${acc}:${id}`;
  if (loading.has(key)) return;
  loading.add(key);
  try {
    putConversation(qc, acc, await getConversation(acc, id), userId);
  } catch {
    // Нет доступа или сеть мигнула — следующее событие или обновление списка поправит.
  } finally {
    loading.delete(key);
  }
}

export function handleAction(qc: QueryClient, acc: number, userId: number, action: RtAction, throttle: () => void): void {
  switch (action.type) {
    case 'conv': {
      const patch = action.patch;
      const res = patchAllLists(qc, acc, patch, userId);
      if (patch.kind === 'snapshot') {
        const snap = patch.conv;
        qc.setQueryData<InboxConversation | undefined>(cwKeys.conv(acc, snap.id), (old) =>
          old ? { ...snap, lastMessage: snap.lastMessage ?? old.lastMessage } : old);
      }
      // Сообщение в переписку, которой нет ни в одном списке: берём её целиком.
      if (res.unknown && res.anyList && patch.kind === 'message') {
        void fetchAndPut(qc, acc, patch.convId, userId);
      }
      return;
    }
    case 'message': {
      updateMessages(qc, acc, action.convId, (s) => applyMessageAction(s, action.message));
      const open = getOpenConversation();
      const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
      if (open && open.accountId === acc && open.id === action.convId && visible
        && action.message.message_type === MESSAGE_INCOMING) {
        markRead(qc, acc, action.convId, userId);
      }
      return;
    }
    case 'unreadCounts':
      throttle();
      return;
    case 'inboxes':
      void qc.invalidateQueries({ queryKey: cwKeys.inboxes(acc) });
      return;
  }
}

/** Дозагрузить пропущенное: всё активное в кэше Переписок перезапрашивается. */
export function resync(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: cwKeys.all, refetchType: 'active' });
}

export function startRealtime(o: RealtimeOptions): RealtimeHandle {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastFetch = 0;
  const refetchUnread = () => {
    lastFetch = Date.now();
    void o.qc.invalidateQueries({ queryKey: cwKeys.unread(o.accountId) });
  };
  const throttled = () => {
    const wait = UNREAD_THROTTLE_MS - (Date.now() - lastFetch);
    if (wait <= 0) { refetchUnread(); return; }
    if (timer) return;
    timer = setTimeout(() => { timer = null; refetchUnread(); }, wait);
  };

  // Счётчики «ждут ответа» в подзаголовке: не чаще раза в 3 с после потока событий.
  let metaTimer: ReturnType<typeof setTimeout> | null = null;
  const metaSoon = () => {
    if (metaTimer) return;
    metaTimer = setTimeout(() => {
      metaTimer = null;
      void o.qc.invalidateQueries({ queryKey: ['cw', 'meta', o.accountId] });
    }, 3000);
  };

  const cable = new Cable({
    url: cableUrl(config.CHATWOOT_URL),
    identifier: { channel: 'RoomChannel', pubsub_token: o.pubsubToken, account_id: o.accountId, user_id: o.userId },
    onEvent: (event, data) => {
      for (const a of decodeEvent(event, data, o.accountId)) {
        handleAction(o.qc, o.accountId, o.userId, a, throttled);
        if (a.type === 'conv' || a.type === 'message') metaSoon();
      }
    },
    onReconnected: () => resync(o.qc),
    onRejected: o.onRejected,
    onState: o.onState,
  });
  cable.start();

  return {
    stop: () => {
      if (timer) clearTimeout(timer);
      if (metaTimer) clearTimeout(metaTimer);
      cable.stop();
    },
    reconnectNow: () => cable.reconnectNow(),
    state: () => cable.getState(),
  };
}
